/**
 * The Month view's plan check (api-server 0.6.55; Akash, 2026-10-31).
 *
 * A WINDOW read like the schedule occurrences: the days of the month on
 * screen, on the site's calendar, are the cache key, so paging back to a
 * month is a cache hit. It changes when the plan does, so every schedule
 * and maintenance write invalidates the whole `plan-check` prefix
 * (`useSchedules.ts::invalidateSchedules`, `useMaintenance.ts`); with
 * nothing written it is read again at most every few minutes, for the
 * cleans that finish meanwhile.
 *
 * ONLY FOR `PLAN_CHECK_ROLES`. The role test is here, in the hook, rather
 * than left to each screen: for anyone else the query is never enabled,
 * so no request is made on their behalf (decision 1). Under "View as" the
 * screen is drawn for the role on show, but reads still go out with the
 * signed-in person's own token, so the request is made only when THEIR
 * role is one it is for too (`usePlanCheckAccess`): a Support L2 viewing
 * as a site supervisor was refused by the server, and told "your role
 * cannot see it" under a banner naming a role that can (review,
 * 2026-10-31).
 */
import { useContext } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { AuthContext } from '../../auth/AuthContext';
import { PLAN_CHECK_ROLES, type RoleToken } from '../../auth/types';
import { useRolePreview } from '../../auth/useRolePreview';
import { ApiClientError, fetchPlanCheck, networkFailureText } from '../v1-client';
import type { PlanCheck, SiteId } from '../v1';

export const PLAN_CHECK_KEY = 'plan-check' as const;

export const planCheckKey = (siteId: string, fromDate: string, toDate: string) =>
  [PLAN_CHECK_KEY, siteId, fromDate, toDate] as const;

/** The days asked about: site-calendar `YYYY-MM-DD`, both inclusive. */
export interface PlanCheckRange {
  fromDate: string;
  toDate: string;
}

/** A fresh answer is kept this long before a focus or remount asks again. */
const PLAN_CHECK_STALE_MS = 2 * 60_000;
/** ...and read again this often while the Month view is open. */
const PLAN_CHECK_REFRESH_MS = 5 * 60_000;

function forPlanCheck(roles: readonly RoleToken[]): boolean {
  return PLAN_CHECK_ROLES.some((r) => roles.includes(r));
}

/** Whether the signed-in role (or the role a "View as" preview shows) is
 *  one the plan check is for. Outside an auth provider -- a component
 *  test, a console -- nobody is. */
export function usePlanCheckAllowed(): boolean {
  return forPlanCheck(useContext(AuthContext)?.roles ?? []);
}

/**
 * Who sees the plan check, and who it is asked for:
 *  - `shown`: the role on screen is one it is for (decision 1);
 *  - `readable`: and the signed-in person's own role is one too, so their
 *    token may read it. Without a preview the two are the same role.
 */
export function usePlanCheckAccess(): { shown: boolean; readable: boolean } {
  const shown = usePlanCheckAllowed();
  const { previewRole, realRoles } = useRolePreview();
  return { shown, readable: shown && (previewRole === null || forPlanCheck(realRoles)) };
}

/** Said in place of the plan check when it is `shown` and not `readable`. */
export const PLAN_CHECK_PREVIEW_NOTE =
  'The plan check is not shown while viewing as another role: your own role cannot see it.';

/**
 * The plan check for `range`, or nothing at all when there is no range
 * (a month wholly in the past has nothing to check) or the viewer's role
 * is not one it is for.
 *
 * Retries only what can heal, once: a 5xx or a dropped connection. A 4xx
 * is the server's answer -- an api-server without the route (404), a
 * window it refuses (400) -- and asking again changes nothing.
 */
export function usePlanCheck(range: PlanCheckRange | null) {
  const siteId = useSafeSiteId();
  const allowed = usePlanCheckAccess().readable;
  return useQuery<PlanCheck>({
    queryKey: planCheckKey(siteId, range?.fromDate ?? '', range?.toDate ?? ''),
    queryFn: ({ signal }) =>
      fetchPlanCheck(siteId as SiteId, range!.fromDate, range!.toDate, signal),
    enabled: Boolean(siteId) && range !== null && allowed,
    staleTime: PLAN_CHECK_STALE_MS,
    refetchInterval: PLAN_CHECK_REFRESH_MS,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => failureCount < 1 && planCheckRetryable(error),
  });
}

/** Whether asking again can help: a server fault or a dropped connection.
 *  A 4xx is the server's answer, and "Try again" under it was a button
 *  that could not work (review, 2026-10-31). */
export function planCheckRetryable(err: unknown): boolean {
  return !(err instanceof ApiClientError && err.status < 500);
}

/** Every cached plan check for the site: a write to the plan changes what
 *  any of them would say. */
export function invalidatePlanCheck(qc: QueryClient, siteId: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: [PLAN_CHECK_KEY, siteId] });
}

/** Why the plan check could not be read, after "Plan check unavailable:".
 *  Specific, never a status code alone: an api-server older than the
 *  check says so rather than "api error (404)". */
export function planCheckErrorText(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 404) return 'this server does not have it yet';
    if (err.status === 403) return 'your role cannot see it';
    const said = err.message && err.message !== 'api error' ? err.message : 'the server refused';
    return `${said} (${err.status})`;
  }
  if (networkFailureText(err)) return 'the server could not be reached';
  return err instanceof Error && err.message ? err.message : 'no answer from the server';
}
